import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, XCircle, ArrowRight } from "lucide-react";
import { getPaymentStatus } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { Logo } from "./Logo";
import { ThankYouSignoff } from "./ThankYou";

const MAX_POLLS = 8;

const Shipping = ({ shipping }) => {
  if (!shipping || !shipping.address) return null;
  const a = shipping.address;
  return (
    <div className="mt-6 text-left bg-[#FAF9F5] border border-[#111111]/15 rounded-sm p-5" data-testid="success-shipping">
      <p className="text-[11px] uppercase tracking-widest text-[#555555] mb-2">Shipping to</p>
      <p className="text-sm text-[#222222] leading-relaxed">
        {shipping.name && <>{shipping.name}<br /></>}
        {a.line1}{a.line1 && <br />}
        {a.line2 && <>{a.line2}<br /></>}
        {[a.city, a.state, a.postal_code].filter(Boolean).join(" ")}<br />
        {a.country}
      </p>
    </div>
  );
};

const DesignKeep = () => null;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [data, setData] = useState(null);

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setData(res);
          setState("paid");
          clear();
          // Meta Pixel Purchase — fire once per order (survives reloads + StrictMode double-poll)
          try {
            const dedupeKey = `fbq-purchase-${sessionId}`;
            if (typeof window.fbq === "function" && !sessionStorage.getItem(dedupeKey)) {
              const items = Array.isArray(res.items) ? res.items : [];
              window.fbq("track", "Purchase", {
                content_ids: items.map((i) => i.product_id).filter(Boolean),
                content_type: "product",
                value: Number(res.total) || 0,
                currency: "USD",
                num_items: items.reduce((n, i) => n + (i.qty || 1), 0),
              });
              sessionStorage.setItem(dedupeKey, "1");
            }
          } catch { /* pixel must never break the success page */ }
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState("timeout");
          return;
        }
        setTimeout(() => poll(attempt + 1), 1800);
      } catch {
        setState("error");
      }
    },
    [sessionId, clear]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <div className="min-h-screen bg-[#ECECE8] flex flex-col">
      <div className="noise-overlay" />
      <div className="p-6">
        <Link to="/" data-testid="success-home-link">
          <Logo />
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
          data-testid="payment-success-page"
        >
          {state === "checking" && (
            <div>
              <Loader2 className="h-12 w-12 text-[#FF4500] mx-auto animate-spin" />
              <p className="mt-6 text-[#555555]">Confirming your payment…</p>
            </div>
          )}

          {state === "paid" && (
            <div>
              <div className="h-16 w-16 rounded-full bg-[#FF4500] flex items-center justify-center mx-auto ember-glow">
                <Check className="h-8 w-8 text-white" strokeWidth={3} />
              </div>
              <h1 className="mt-6 font-display font-black text-4xl uppercase tracking-tighter text-[#111111]">
                Order confirmed.
              </h1>
              <p className="text-[#555555] mt-3 text-sm flex items-center justify-center gap-2 flex-wrap">
                <span>Payment confirmed · order #{data?.order_id?.slice(0, 8)}</span>
                {sessionId?.startsWith("paypal-") ? (
                  <span
                    data-testid="success-processor-badge"
                    className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#009cde]/15 text-[#0070ba] text-[10px] font-bold uppercase tracking-widest"
                  >
                    via PayPal
                  </span>
                ) : (
                  <span
                    data-testid="success-processor-badge"
                    className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#635BFF]/15 text-[#635BFF] text-[10px] font-bold uppercase tracking-widest"
                  >
                    via Stripe
                  </span>
                )}
              </p>
              <div className="mt-8 bg-[#FAF9F5] border-2 border-[#111111] shadow-[6px_6px_0px_#111111] rounded-sm p-8">
                <div className="font-display font-black text-5xl text-[#FF4500]" data-testid="success-total">
                  ${Number(data?.total || 0).toFixed(2)}
                </div>
                <div className="text-xs uppercase tracking-widest text-[#555555] mt-1">
                  Order Total
                </div>
              </div>
              <p className="text-xs text-[#555555] mt-6">
                Receipt sent to {data?.email}. Your gear ships fast.
              </p>
              <Shipping shipping={data?.shipping} />
              <DesignKeep items={data?.items} />
              <Link
                to="/#store"
                data-testid="success-track-link"
                className="mt-6 inline-flex items-center gap-2 bg-[#FF4500] text-white px-7 py-3.5 rounded-sm font-black uppercase text-sm tracking-wider hover:bg-[#111111] transition-colors"
              >
                Back to the Store <ArrowRight className="h-4 w-4" />
              </Link>
              <ThankYouSignoff />
            </div>
          )}

          {(state === "timeout" || state === "error") && (
            <div>
              <XCircle className="h-12 w-12 text-[#555555] mx-auto" />
              <h1 className="mt-6 font-display font-black text-3xl uppercase tracking-tighter text-[#111111]">
                Still processing
              </h1>
              <p className="text-[#555555] mt-3 text-sm">
                Your payment is taking a moment. Your receipt will land in your email shortly.
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 border-2 border-[#111111] text-[#111111] px-7 py-3.5 rounded-sm font-bold uppercase text-sm tracking-wider hover:border-[#FF4500] hover:text-[#FF4500] transition-colors"
              >
                Back Home
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="min-h-screen bg-[#ECECE8] flex flex-col">
    <div className="noise-overlay" />
    <div className="p-6">
      <Link to="/">
        <Logo />
      </Link>
    </div>
    <div className="flex-1 flex items-center justify-center px-5">
      <div className="text-center max-w-md" data-testid="payment-cancel-page">
        <XCircle className="h-12 w-12 text-[#555555] mx-auto" />
        <h1 className="mt-6 font-display font-black text-4xl uppercase tracking-tighter text-[#111111]">
          Checkout cancelled
        </h1>
        <p className="text-[#555555] mt-3">
          No worries — your cart is still waiting. Jump back in whenever you're ready.
        </p>
        <Link
          to="/#store"
          data-testid="cancel-back-link"
          className="mt-6 inline-flex items-center gap-2 bg-[#FF4500] text-white px-7 py-3.5 rounded-sm font-black uppercase text-sm tracking-wider hover:bg-[#111111] transition-colors"
        >
          Back to Store
        </Link>
      </div>
    </div>
  </div>
);
